/**
 * SCRUM-377 — Historial de trabajos de impresión (etiquetas + cartelería) y
 * "repetir": navega a la misma vista de impresión del trabajo guardado, que
 * ya re-resuelve los precios actuales contra la lista elegida (nunca
 * reimprime un precio congelado).
 */
import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ClinicalPageShell } from "@/components/clinical";
import { CrudShellHeader, DataTable, type DataTableColumn } from "@/components/ds";
import { Button } from "@/components/ui/button";
import { apiGetJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { useTenantProductFlags } from "@/hooks/use-tenant-product-flags";
import { FeatureLockedState } from "@/components/workspace/FeatureLockedState";
import { LABEL_TEMPLATES, isLabelTemplateKey } from "@/lib/shelf-labels/templates";
import { PROMO_TEMPLATES, isPromoTemplateKey } from "@/lib/promo-signage/templates";

export const Route = createFileRoute("/app/catalogacion/historial-impresiones")({
  component: PrintJobsHistoryPage,
  head: () => ({ meta: [{ title: "Historial de impresiones — Structura Market" }] }),
});

interface PrintJob {
  id: string;
  kind: "labels" | "promo_signage";
  priceListId: string | null;
  productCount: number;
  templateKey: string;
  createdAt: string;
}

function templateName(job: PrintJob): string {
  if (job.kind === "promo_signage") return isPromoTemplateKey(job.templateKey) ? PROMO_TEMPLATES[job.templateKey].name : job.templateKey;
  return isLabelTemplateKey(job.templateKey) ? LABEL_TEMPLATES[job.templateKey].name : job.templateKey;
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
}

function PrintJobsHistoryPage() {
  const tid = useWorkspaceTenantId();
  const productFlags = useTenantProductFlags();
  const enabled = productFlags?.shelf_labels === true;

  const [jobs, setJobs] = useState<PrintJob[]>([]);
  const [priceListNames, setPriceListNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tid || !enabled) return;
    setLoading(true);
    Promise.all([
      apiGetJson<{ jobs?: PrintJob[] }>("/api/shelf-labels/print-jobs", tid),
      apiGetJson<{ lists?: Array<{ id: string; name: string }> }>("/api/products/price-lists", tid).catch(() => ({ lists: [] })),
    ])
      .then(([jobsRes, plRes]) => {
        setJobs(jobsRes.jobs ?? []);
        setPriceListNames(Object.fromEntries((plRes.lists ?? []).map((l) => [l.id, l.name])));
      })
      .finally(() => setLoading(false));
  }, [tid, enabled]);

  if (!enabled) {
    return (
      <FeatureLockedState
        payload={{
          code: "plan_feature_locked",
          feature: "shelf_labels",
          message: "Etiquetas de góndola y cartelería promocional está disponible en el plan Pro.",
        }}
      />
    );
  }

  const columns: DataTableColumn<PrintJob>[] = [
    { key: "createdAt", header: "Fecha", render: (j) => formatDateTime(j.createdAt) },
    { key: "kind", header: "Tipo", render: (j) => (j.kind === "promo_signage" ? "Cartelería" : "Etiquetas") },
    { key: "template", header: "Template", render: (j) => templateName(j) },
    { key: "priceList", header: "Lista de precios", render: (j) => (j.priceListId ? (priceListNames[j.priceListId] ?? "—") : "Precio propio") },
    { key: "count", header: "Productos", className: "text-right", render: (j) => j.productCount },
    {
      key: "actions",
      header: "",
      className: "w-32",
      render: (j) => (
        <Button asChild variant="outline" size="sm" data-testid={`print-job-repeat-${j.id}`}>
          <Link
            to={j.kind === "promo_signage" ? "/print/carteleria/$jobId" : "/print/etiquetas/$jobId"}
            params={{ jobId: j.id }}
            search={{ tid }}
          >
            Repetir
          </Link>
        </Button>
      ),
    },
  ];

  return (
    <div>
      <CrudShellHeader title="Historial de impresiones" description="Etiquetas y cartelería generadas — repetí una configuración con precios actualizados" />
      <ClinicalPageShell offset="none" className="pt-6">
        <DataTable
          columns={columns}
          rows={jobs}
          getRowKey={(j) => j.id}
          emptyState={
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">
              {loading ? "Cargando…" : "Todavía no generaste etiquetas ni cartelería"}
            </p>
          }
        />
      </ClinicalPageShell>
    </div>
  );
}
